/**
 * De tekst op de ingeklapte reactietegel (JP, keuze c van 22-09).
 *
 * De tegel start als één regel. Wat daar links staat moet in één
 * oogopslag zeggen wat er klaarligt, zonder dat je hem opent:
 *
 * - een voorstel van Sanne of een klaargezette follow-up;
 * - je eigen tekst, of een leeg veld om te beginnen;
 * - of: deze zaak is al af, en dan hoe en door wie.
 *
 * Bewust een eigen functie met eigen toetsen: dit zinnetje is het enige
 * wat JP ziet zolang de tegel dicht is, en een fout erin is een fout in
 * het hele scherm.
 */

export interface KopregelStand {
  /** De tab die openstaat: bij een notitie zegt de kop dat ook. */
  tab: "klant" | "notitie";
  /** De zaak is afgerond; er kan alleen nog een nieuw bericht bij. */
  afgerond: boolean;
  /** Afgerond, maar zonder mail uit het portaal. */
  afgerondZonderMail: boolean;
  /** "Sanne", "Nova", "jou" — of null als niemand het vastlegde. */
  verstuurdDoor: string | null;
  /** Amsterdamse kloktijd "09:17", of null. */
  verstuurdOm: string | null;
  /** "sanne", "nova", "wekker", "verstuurd" of "leeg". */
  bron: string;
  /** Sessie 46 (B4): er wordt nu een voorstel geschreven. */
  voorstelBezig?: boolean;
  voorstelDoor?: string | null;
  /** Sessie 47 (2b): het voorstel is afronden zonder antwoord ("bedankje"). */
  afrondenLabel?: string | null;
  /** Sessie 47 (2a): "Afgerond door JP, bedankje". */
  afgerondWaarom?: string | null;
  /** Sessie 48 (A): een telefoonzaak heeft niets om te versturen. */
  telefoon?: boolean;
}

export function kopregelTekst(stand: KopregelStand): string {
  if (stand.tab === "notitie") return "Interne notitie";

  if (stand.afgerond) {
    if (stand.afgerondWaarom) return `${stand.afgerondWaarom} · geen antwoord verstuurd`;
    if (stand.afgerondZonderMail) {
      return "Afgerond zonder mail uit het portaal";
    }
    // "Verstuurd door Sanne om 09:17" (JP, punt 1), met het woord
    // Afgerond ervoor zodat de regel ook zegt dat de zaak dicht is
    // (punt 3). Ontbreekt wie of wanneer, dan blijft dat weg: leeg
    // blijft leeg, nooit een verzonnen naam (les 22-09).
    const delen: string[] = [];
    if (stand.verstuurdDoor) delen.push(`door ${stand.verstuurdDoor}`);
    if (stand.verstuurdOm) delen.push(`om ${stand.verstuurdOm}`);
    if (delen.length === 0) return "Afgerond";
    return `Afgerond · verstuurd ${delen.join(" ")}`;
  }

  // Sessie 47 (2b): het verhaal is rond; geen antwoord maar afronden.
  if (stand.afrondenLabel) {
    const wie = stand.voorstelDoor === "nova" ? "Nova" : "Sanne";
    return `Voorstel van ${wie}: afronden zonder antwoord, ${stand.afrondenLabel}`;
  }
  if (stand.bron === "sanne") return "Voorstel van Sanne klaar";
  // Sessie 46 (B4): het voorstel van Nova voor een chat of WhatsApp.
  if (stand.bron === "nova") return "Voorstel van Nova klaar";
  if (stand.voorstelBezig) {
    return stand.bron === "leeg" && stand.voorstelDoor === "nova"
      ? "Nova schrijft een voorstel…"
      : "Sanne schrijft een voorstel…";
  }
  if (stand.bron === "wekker") return "Follow-up staat klaar";
  if (stand.telefoon) return "Telefoongesprek · terugbellen of afronden";
  return "Jouw antwoord";
}
